import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { 
  Layers, Plus, ArrowLeft, Edit, Trash2, Save, ChevronRight, Search, FolderTree
} from 'lucide-react';
import { toast } from 'sonner';

const TopicsManagement = () => {
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [subtopics, setSubtopics] = useState([]);
  const [boards, setBoards] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('topics');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedTopics, setExpandedTopics] = useState({});
  
  const [topicForm, setTopicForm] = useState({
    name: '',
    code: '',
    board_id: '',
    subject_id: '',
    paper_id: ''
  });

  const [subtopicForm, setSubtopicForm] = useState({
    name: '',
    code: '',
    topic_id: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [topicsRes, subtopicsRes, boardsRes, subjectsRes, papersRes] = await Promise.all([
        api.get('/question-bank/topics'),
        api.get('/question-bank/subtopics'),
        api.get('/boards'),
        api.get('/subjects'),
        api.get('/question-bank/papers')
      ]);
      setTopics(topicsRes.data);
      setSubtopics(subtopicsRes.data);
      setBoards(boardsRes.data);
      setSubjects(subjectsRes.data);
      setPapers(papersRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitTopic = async (e) => {
    e.preventDefault();
    const loadingToast = toast.loading(editingItem ? 'Updating topic...' : 'Creating topic...');
    
    try {
      if (editingItem) {
        await api.put(`/question-bank/topics/${editingItem.id}`, topicForm);
        toast.dismiss(loadingToast);
        toast.success('Topic updated successfully');
      } else {
        await api.post('/question-bank/topics', topicForm);
        toast.dismiss(loadingToast);
        toast.success('Topic created successfully');
      }
      setShowModal(false);
      setEditingItem(null);
      setTopicForm({ name: '', code: '', board_id: '', subject_id: '', paper_id: '' });
      fetchData();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(err.response?.data?.detail || 'Failed to save topic');
    }
  };

  const handleSubmitSubtopic = async (e) => {
    e.preventDefault();
    const loadingToast = toast.loading(editingItem ? 'Updating subtopic...' : 'Creating subtopic...');
    
    try {
      if (editingItem) {
        await api.put(`/question-bank/subtopics/${editingItem.id}`, subtopicForm);
        toast.dismiss(loadingToast);
        toast.success('Subtopic updated successfully');
      } else {
        await api.post('/question-bank/subtopics', subtopicForm);
        toast.dismiss(loadingToast);
        toast.success('Subtopic created successfully');
      }
      setShowModal(false);
      setEditingItem(null);
      setSubtopicForm({ name: '', code: '', topic_id: '' });
      fetchData();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(err.response?.data?.detail || 'Failed to save subtopic');
    }
  };

  const handleEditTopic = (topic) => {
    setEditingItem(topic);
    setTopicForm({
      name: topic.name || '',
      code: topic.code || '',
      board_id: topic.board_id || '',
      subject_id: topic.subject_id || '',
      paper_id: topic.paper_id || ''
    });
    setActiveTab('topics');
    setShowModal(true);
  };

  const handleEditSubtopic = (subtopic) => {
    setEditingItem(subtopic);
    setSubtopicForm({
      name: subtopic.name || '',
      code: subtopic.code || '',
      topic_id: subtopic.topic_id || ''
    });
    setActiveTab('subtopics');
    setShowModal(true);
  };

  const handleDeleteTopic = async (topicId, topicName) => {
    if (!window.confirm(`Are you sure you want to delete "${topicName}"? This will also delete all subtopics.`)) return;
    
    try {
      await api.delete(`/question-bank/topics/${topicId}`);
      toast.success('Topic deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete topic');
    }
  };

  const handleDeleteSubtopic = async (subtopicId, subtopicName) => {
    if (!window.confirm(`Are you sure you want to delete "${subtopicName}"?`)) return;
    
    try {
      await api.delete(`/question-bank/subtopics/${subtopicId}`);
      toast.success('Subtopic deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete subtopic');
    }
  };

  const toggleTopic = (topicId) => {
    setExpandedTopics(prev => ({
      ...prev,
      [topicId]: !prev[topicId]
    }));
  };

  const getBoardName = (boardId) => boards.find(b => b.id === boardId)?.name || 'Unknown';
  const getSubjectName = (subjectId) => subjects.find(s => s.id === subjectId)?.name || 'Unknown';
  const getPaperName = (paperId) => {
    const paper = papers.find(p => p.id === paperId);
    return paper ? `${paper.code} - ${paper.name}` : 'Unknown';
  };
  const getTopicName = (topicId) => topics.find(t => t.id === topicId)?.name || 'Unknown';

  const getSubtopicsForTopic = (topicId) => subtopics.filter(st => st.topic_id === topicId);

  const filteredTopics = topics.filter(t => 
    t.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading topics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-700 text-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <button 
            onClick={() => navigate('/qbm/dashboard')}
            className="flex items-center gap-2 text-green-100 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Question Bank
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Layers className="w-8 h-8" />
                Topics & Subtopics
              </h1>
              <p className="mt-2 text-green-200">Organize your question bank by topics</p>
            </div>
            <button
              onClick={() => {
                setEditingItem(null);
                if (activeTab === 'topics') {
                  setTopicForm({ name: '', code: '', board_id: '', subject_id: '', paper_id: '' });
                } else {
                  setSubtopicForm({ name: '', code: '', topic_id: '' });
                }
                setShowModal(true);
              }}
              className="flex items-center gap-2 px-6 py-3 bg-white text-green-700 rounded-lg font-medium hover:bg-green-50 transition-all"
              data-testid="create-topic-btn"
            >
              <Plus className="w-5 h-5" />
              Add {activeTab === 'topics' ? 'Topic' : 'Subtopic'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => setActiveTab('topics')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'topics'
                ? 'bg-green-600 text-white'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            Topics ({topics.length})
          </button>
          <button
            onClick={() => setActiveTab('subtopics')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'subtopics'
                ? 'bg-green-600 text-white'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            Subtopics ({subtopics.length})
          </button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder={`Search ${activeTab}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full h-11 pl-10 pr-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>

        {/* Topics View */}
        {activeTab === 'topics' && (
          <div className="space-y-4">
            {filteredTopics.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                <FolderTree className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500">No topics found</p>
                <p className="text-sm text-slate-400 mt-1">Create a topic to organize your questions</p>
              </div>
            ) : (
              filteredTopics.map(topic => {
                const topicSubtopics = getSubtopicsForTopic(topic.id);
                const isExpanded = expandedTopics[topic.id];
                
                return (
                  <div key={topic.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <div 
                      className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50"
                      onClick={() => toggleTopic(topic.id)}
                    >
                      <div className="flex items-center gap-4">
                        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm bg-green-50 text-green-700 px-2 py-0.5 rounded">
                              {topic.code}
                            </span>
                            <span className="font-medium text-slate-900">{topic.name}</span>
                            <span className="text-xs text-slate-400">({topicSubtopics.length} subtopics)</span>
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                            <span>{getBoardName(topic.board_id)}</span>
                            <span>•</span>
                            <span>{getSubjectName(topic.subject_id)}</span>
                            {topic.paper_id && (
                              <>
                                <span>•</span>
                                <span>{getPaperName(topic.paper_id)}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => handleEditTopic(topic)}
                          className="p-2 text-slate-500 hover:text-green-600 hover:bg-green-50 rounded transition-all"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteTopic(topic.id, topic.name)}
                          className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    {isExpanded && topicSubtopics.length > 0 && (
                      <div className="border-t border-slate-200 bg-slate-50 divide-y divide-slate-200">
                        {topicSubtopics.map(subtopic => (
                          <div key={subtopic.id} className="flex items-center justify-between px-4 py-3 pl-12">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded">
                                {subtopic.code}
                              </span>
                              <span className="text-slate-700">{subtopic.name}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleEditSubtopic(subtopic)}
                                className="p-1.5 text-slate-500 hover:text-green-600 hover:bg-green-50 rounded transition-all"
                                title="Edit"
                              >
                                <Edit className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => handleDeleteSubtopic(subtopic.id, subtopic.name)}
                                className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                                title="Delete"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {isExpanded && topicSubtopics.length === 0 && (
                      <div className="border-t border-slate-200 bg-slate-50 p-4 pl-12 text-sm text-slate-500">
                        No subtopics yet. 
                        <button 
                          onClick={() => {
                            setActiveTab('subtopics');
                            setSubtopicForm({ name: '', code: '', topic_id: topic.id });
                            setShowModal(true);
                          }}
                          className="text-green-600 hover:underline ml-1"
                        >
                          Add one
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Subtopics View */}
        {activeTab === 'subtopics' && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Code</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Name</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Parent Topic</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {subtopics
                  .filter(st => 
                    st.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    st.code?.toLowerCase().includes(searchTerm.toLowerCase())
                  )
                  .map(subtopic => (
                  <tr key={subtopic.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4">
                      <span className="font-mono text-sm bg-emerald-50 text-emerald-700 px-2 py-1 rounded">
                        {subtopic.code}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-medium text-slate-900">{subtopic.name}</td>
                    <td className="px-6 py-4 text-slate-600">{getTopicName(subtopic.topic_id)}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleEditSubtopic(subtopic)}
                          className="p-2 text-slate-500 hover:text-green-600 hover:bg-green-50 rounded transition-all"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteSubtopic(subtopic.id, subtopic.name)}
                          className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {subtopics.length === 0 && (
                  <tr>
                    <td colSpan="4" className="px-6 py-12 text-center text-slate-500">
                      <Layers className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p>No subtopics found</p>
                      <p className="text-sm mt-1">Create a subtopic under any topic</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900">
                {editingItem 
                  ? `Edit ${activeTab === 'topics' ? 'Topic' : 'Subtopic'}` 
                  : `Create New ${activeTab === 'topics' ? 'Topic' : 'Subtopic'}`
                }
              </h2>
            </div>
            
            {activeTab === 'topics' ? (
              <form onSubmit={handleSubmitTopic} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Topic Code</label>
                    <input
                      type="text"
                      required
                      value={topicForm.code}
                      onChange={(e) => setTopicForm({ ...topicForm, code: e.target.value })}
                      className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="e.g., T1, T2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Topic Name</label>
                    <input
                      type="text"
                      required
                      value={topicForm.name}
                      onChange={(e) => setTopicForm({ ...topicForm, name: e.target.value })}
                      className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="e.g., Number Systems"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Board</label>
                    <select
                      required
                      value={topicForm.board_id}
                      onChange={(e) => setTopicForm({ ...topicForm, board_id: e.target.value })}
                      className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="">Select Board</option>
                      {boards.map(board => (
                        <option key={board.id} value={board.id}>{board.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Subject</label>
                    <select
                      required
                      value={topicForm.subject_id}
                      onChange={(e) => setTopicForm({ ...topicForm, subject_id: e.target.value })}
                      className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="">Select Subject</option>
                      {subjects.map(subject => (
                        <option key={subject.id} value={subject.id}>{subject.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Paper (Optional)</label>
                  <select
                    value={topicForm.paper_id}
                    onChange={(e) => setTopicForm({ ...topicForm, paper_id: e.target.value })}
                    className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="">All Papers</option>
                    {papers
                      .filter(p => !topicForm.board_id || p.board_id === topicForm.board_id)
                      .filter(p => !topicForm.subject_id || p.subject_id === topicForm.subject_id)
                      .map(paper => (
                        <option key={paper.id} value={paper.id}>{paper.code} - {paper.name}</option>
                      ))
                    }
                  </select>
                </div>

                <div className="flex items-center justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => { setShowModal(false); setEditingItem(null); }}
                    className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-all"
                  >
                    <Save className="w-4 h-4" />
                    {editingItem ? 'Save Changes' : 'Create Topic'}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleSubmitSubtopic} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Subtopic Code</label>
                    <input
                      type="text"
                      required
                      value={subtopicForm.code}
                      onChange={(e) => setSubtopicForm({ ...subtopicForm, code: e.target.value })}
                      className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="e.g., ST1, ST2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Subtopic Name</label>
                    <input
                      type="text"
                      required
                      value={subtopicForm.name}
                      onChange={(e) => setSubtopicForm({ ...subtopicForm, name: e.target.value })}
                      className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="e.g., Prime Numbers"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Parent Topic</label>
                  <select
                    required
                    value={subtopicForm.topic_id}
                    onChange={(e) => setSubtopicForm({ ...subtopicForm, topic_id: e.target.value })}
                    className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="">Select Topic</option>
                    {topics.map(topic => (
                      <option key={topic.id} value={topic.id}>{topic.code} - {topic.name}</option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => { setShowModal(false); setEditingItem(null); }}
                    className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-all"
                  >
                    <Save className="w-4 h-4" />
                    {editingItem ? 'Save Changes' : 'Create Subtopic'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TopicsManagement;
